import type {
  AbsenceRow,
  AppSettingRow,
  DayOffWishRow,
  NotificationRow,
  MachineRow,
  MaterialTypeRow,
  ProductSizeRow,
  RecipeRow,
  ShiftAssignmentRow,
  ShiftRow,
  ShiftTemplateRow,
  MachineNormRow,
  SiteNormRow,
  SiteRow,
  UserRow,
} from '@pms/shared';
import Dexie, { type Table } from 'dexie';

import type { CachedCredential } from '../auth/credentials.js';
import type { AttendanceList } from '../attendance/types.js';
import type { PhotoRecord } from '../photos/types.js';
import type { ReportDraft } from '../reports/draft.js';
import type { MetaRecord, OutboxRecord } from './types.js';

/**
 * Локальная база устройства.
 *
 * Всё, что здесь лежит, переживает перезагрузку телефона — на этом держится
 * обещание «данные не теряются никогда». Очередь чистится только после
 * подтверждения сервера, справочники — только по приходу новых версий.
 */
export class PmsDatabase extends Dexie {
  outbox!: Table<OutboxRecord, string>;
  /** Отдельная очередь фото — с низким приоритетом, цифры отчёта уходят первыми. */
  photos!: Table<PhotoRecord, string>;
  /** Черновики сменных отчётов: мастер сохраняется после каждого шага. */
  drafts!: Table<ReportDraft, string>;
  /** Списки записанных на экскурсию — чтобы отмечать явку без сети. */
  attendanceLists!: Table<AttendanceList, string>;
  /** Кто уже входил на этом устройстве — для входа без сети. */
  credentials!: Table<CachedCredential, string>;
  meta!: Table<MetaRecord, string>;

  sites!: Table<SiteRow, string>;
  machines!: Table<MachineRow, string>;
  shiftTemplates!: Table<ShiftTemplateRow, string>;
  productSizes!: Table<ProductSizeRow, string>;
  materialTypes!: Table<MaterialTypeRow, string>;
  recipes!: Table<RecipeRow, string>;
  siteNorms!: Table<SiteNormRow, string>;
  machineNorms!: Table<MachineNormRow, string>;
  appSettings!: Table<AppSettingRow, string>;
  users!: Table<UserRow, string>;
  shifts!: Table<ShiftRow, string>;
  shiftAssignments!: Table<ShiftAssignmentRow, string>;
  dayOffWishes!: Table<DayOffWishRow, string>;
  absences!: Table<AbsenceRow, string>;
  /** Колокольчик работает офлайн — уведомления лежат на устройстве. */
  notifications!: Table<NotificationRow, string>;

  constructor(name = 'pms') {
    super(name);
    this.version(1).stores({
      outbox: 'id, status, createdAt',
      meta: 'key',

      sites: 'id, code, sortOrder',
      machines: 'id, siteId, code, sortOrder',
      shiftTemplates: 'id, siteId, shiftNumber',
      productSizes: 'id, code, sortOrder',
      materialTypes: 'id, code, sortOrder',
      recipes: 'id',
      siteNorms: 'id, siteId',
      appSettings: 'key',
      users: 'id, role, fullName',
      shifts: 'id, siteId, date, [siteId+date]',
      shiftAssignments: 'id, shiftId, userId, machineId',
    });

    this.version(2).stores({
      photos: 'id, status, reportId, createdAt',
    });

    this.version(3).stores({
      drafts: 'id, updatedAt, siteId',
    });

    this.version(4).stores({
      dayOffWishes: 'id, userId, date, weekPlanId',
      absences: 'id, userId, shiftId, status',
      notifications: 'id, createdAt, readAt, type',
    });

    // Списки записанных на экскурсию кэшируются отдельно и точечно:
    // это персональные данные кандидатов, им незачем ехать на устройство
    // вместе с общими справочниками
    this.version(5).stores({
      attendanceLists: 'date',
    });

    // Вход без сети: устройство помнит тех, кто на нём уже входил при связи.
    // Хэш считает оно само — с сервера учётные данные не приезжают.
    this.version(6).stores({
      credentials: 'login, userId',
    });

    // Нормы выхода переехали с участка на агрегат: на одном участке гильотина
    // режет без отходов, а дробилка рядом даёт 90%
    this.version(7).stores({
      machineNorms: 'id, machineId',
    });
  }
}

export const db = new PmsDatabase();

/**
 * Просим браузер не вычищать локальные данные при нехватке места.
 * Без этого очередь неотправленных отчётов может исчезнуть вместе с кэшем.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row ? (row.value as T) : undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/** Идентификатор устройства: создаётся один раз и живёт вместе с базой. */
export async function getDeviceId(): Promise<string> {
  const existing = await getMeta<string>('deviceId');
  if (existing) return existing;
  const created = crypto.randomUUID();
  await setMeta('deviceId', created);
  return created;
}
